import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Alert } from '../components/ui/Alert.tsx';
import { Brand, CenteredPage, Spinner } from '../components/ui/Layout.tsx';
import { AuthProvider, useAuth } from '../lib/auth.tsx';
import { envResult } from '../lib/env.ts';
import { ThemeProvider } from '../lib/theme-context.tsx';
import { createAppRouter } from './router.tsx';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true } },
  });
}

function RoutedApp() {
  const auth = useAuth();
  const [router] = useState(() => createAppRouter(auth));

  // Route guards read the auth state from the router context: re-run them when it changes.
  useEffect(() => {
    router.update({ context: { auth } });
    void router.invalidate();
  }, [router, auth]);

  if (auth.status === 'loading') {
    return (
      <CenteredPage>
        <Spinner label="Starting Fundline" />
      </CenteredPage>
    );
  }
  return <RouterProvider router={router} context={{ auth }} />;
}

function NotConfigured({ problems }: { problems: string[] }) {
  return (
    <CenteredPage>
      <Brand />
      <Alert tone="error">
        <p className="font-semibold">This copy of the app isn't set up yet.</p>
        <ul className="mt-1 list-disc pl-5">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <p className="mt-2">Set the VITE_* variables (see .env.example) and rebuild.</p>
      </Alert>
    </CenteredPage>
  );
}

function Crashed() {
  return (
    <CenteredPage>
      <Brand />
      <Alert tone="error">
        Something went wrong. Reload the page. If it keeps happening, tell your admin.
      </Alert>
    </CenteredPage>
  );
}

export function App() {
  const [queryClient] = useState(makeQueryClient);
  if (!envResult.ok) return <NotConfigured problems={envResult.problems} />;
  return (
    <Sentry.ErrorBoundary fallback={<Crashed />}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <RoutedApp />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
